import React, { useCallback, useMemo } from 'react';
import { DataTable } from 'components/data-table';
import { useFormationSlotsQuery } from './hooks';
import type { FormationSlot } from 'types/api-responses';
import style from './type-curve-wells-table.module.scss';
import { slotTableColumns } from './slot-table-columns';

interface SlotTableProps {
  typeCurveHeaderDetailsId: number;
  collectionIds?: number[];
}

export const SlotTable: React.FC<SlotTableProps> = ({
  typeCurveHeaderDetailsId,
  collectionIds,
}) => {
  // Both the Type Curve pages and the Inventory Workspace source the slot table
  // from the same per-unit query (/queries/development_area_formation_slots),
  // scoped by the selected development area and the active type curve header.
  // `isFetching` is true on the initial load AND on a refetch (section line
  // moved, re-slot landed) — so the DataTable's own loading view shows whenever
  // the slot-table API is in flight.
  const { slots, isFetching, error } = useFormationSlotsQuery(
    typeCurveHeaderDetailsId,
    collectionIds
  );

  // Build a stable rowId lookup keyed by row reference. This runs on every data
  // change (which is correct — we need fresh IDs when slots are replaced).
  // Using a WeakMap avoids memory leaks since entries are GC'd with their rows.
  const rowIdMap = useMemo(() => {
    const map = new WeakMap<FormationSlot, string>();
    const used = new Set<string>();

    for (const row of slots) {
      // Joined on `|`, which cannot occur in these values — `-` can, and does:
      // both `developmentAreaName` ("U-0000062323") and `slotId`
      // ("U-0000062323_MSS_2") carry dashes, so a `-` join let `a-b` + `c` and
      // `a` + `b-c` compose the same key from different rows.
      //
      // `xOffsetFt` / `tvdFt` are what actually separate sibling slots of one
      // formation: for an LTD slot `uwi` is "--" and the area and formation are
      // shared, so without geometry the key rests on `slotId` alone and collapses
      // to the literal "no-slot" when that is null.
      //
      // `slotNumber` is what separates the LANE slots of one crossing well: a well
      // that runs through two drainage lanes gets a row per lane, and both carry
      // its bare UWI as `slotId`, the same unit and the same formation. Each lane
      // row now sits on the crossing that cut it, so `xOffsetFt` usually differs
      // too — but a U-turn can cross the section at one x in both lanes, and the
      // lane is the row's real identity either way. All six fields below once
      // matched on well 4232947879, whose two JMS rows differed only in lane. The
      // suffix loop would still have made the ids unique, but on identity that is
      // positional rather than real — so the lane goes in the key itself.
      const base = [
        row.developmentAreaName,
        row.formation ?? 'no-formation',
        row.uwi || 'no-uwi',
        row.slotId ?? 'no-slot',
        row.slotNumber ?? 'no-slot-number',
        row.xOffsetFt ?? 'no-x',
        row.tvdFt ?? 'no-tvd',
      ].join('|');

      // Uniqueness is a property of this loop, not of the data: ask the set
      // rather than trust a counter. The previous counter recorded against the
      // suffixed key instead of the original, so from the third identical row on
      // every row was handed the same `-1` id — duplicate React keys, which is
      // what made rows appear twice or bind to the wrong slot once the search box
      // re-filtered the list.
      let id = base;
      let n = 1;
      while (used.has(id)) {
        id = `${base}#${n}`;
        n += 1;
      }
      used.add(id);
      map.set(row, id);
    }

    return map;
  }, [slots]);

  const rowIdFn = useCallback(
    (row: FormationSlot) => rowIdMap.get(row) ?? 'unknown',
    [rowIdMap]
  );

  return (
    <div className={style.tableContainer}>
      <DataTable<FormationSlot>
        data={slots}
        columns={slotTableColumns}
        getRowId={rowIdFn}
        isLoading={isFetching}
        error={error}
        persistenceKey="type-curve-slot-table"
        scroll={{ mode: 'paginated' }}
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: true,
        }}
        searchPlaceholder="Search slots..."
      />
    </div>
  );
};
